import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IQuestionnaireGroup } from '@interfaces/interfaces';
import { WorkflowItemDataDirective } from '@workflow/directives/workflow-item-data/workflow-item-data.directive';
import { combineLatest, Observable, Subscription } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { SessionItemData } from '@workflow/services/session-item-data';
import { SpecialQuestionService } from '@workflow/components/questionnaire/questionnaire-item/special-question/special-question.service';
import { SpecialQuestionItem } from '@interfaces/questions';


@Component({
	selector: 'workflow-missing-value-button',
	templateUrl: './missing-value-button.component.html',
	styleUrls: ['./missing-value-button.component.scss']
})
export class MissingValueButtonComponent implements OnInit, OnDestroy {

	@Input() finishedAndLocked = false;
	@Input() disabled: boolean;
	@Input() group: IQuestionnaireGroup;

	hasAnyMissingValue: boolean;
	hasMissingValueKeys: boolean;
	missingValueKeysAmount: number;
	missingValueKeyLabelPairs: {
		key: string;
		title: string
	}[];

	private subscriptions: Subscription;

	constructor(private workflowItemData: WorkflowItemDataDirective, private specialQuestionService: SpecialQuestionService) {
		this.subscriptions = new Subscription();
	}

	ngOnInit(): void {
		this.missingValueKeyLabelPairs = [];
		if (this.group?.missingValueKey) {
			this.missingValueKeyLabelPairs.push({ key: this.group.missingValueKey, title: this.group.title });
		}
		if (this.group?.items?.length) {
			this.missingValueKeyLabelPairs.push(...this.group.items.filter(item => item.missingValueKey).map(item => ({ key: item.missingValueKey, title: item.title})));
		}
		if (this.group?.items) {
			for (const item of this.group.items) {
				if (item.type !== 'BlockQuestion') {
					const itemSpecial = item as SpecialQuestionItem;
					const specialQuestionImpl = this.specialQuestionService.getSpecialQuestion(itemSpecial);
					const baseKey = itemSpecial.baseKey || itemSpecial.key;
					if (specialQuestionImpl.missingValueKeyGroup) {
						this.pushIfAbsent({ key: `${baseKey}.${specialQuestionImpl.missingValueKeyGroup}`, title: specialQuestionImpl.defaultGroupConfiguration.title });
					}

					this.pushArrayIfAbsent(specialQuestionImpl.buildWizardPages(this.group, itemSpecial, 0, {}).filter(p => p.missingValueKey)
						.map(p => ({ key: `${baseKey}.${p.missingValueKey}`, title: p.title })));
				}
			}
		}
		const data$ = this.finishedAndLocked ? this.workflowItemData.itemFull$ : this.workflowItemData.item$;
		this.subscriptions.add(data$.pipe(
			switchMap(data => combineLatest(
				this.relevantKeyPipes(data, this.missingValueKeyLabelPairs.map(p => p.key)),
			)),
		).subscribe(arrayData => {
			this.missingValueKeysAmount = arrayData.length;
			this.hasAnyMissingValue = arrayData.some(data => !!data);
		}));

		this.hasMissingValueKeys = !!this.missingValueKeyLabelPairs.length;
	}

	ngOnDestroy(): void {
		this.subscriptions.unsubscribe();
	}

	private relevantKeyPipes(data: SessionItemData, relevantKeys: string[]): Observable<boolean>[] {
		const obs = [];
		for (let i = 0; i < relevantKeys.length; i++) {
			const pipe = data?.property$(relevantKeys[i]).pipe(
				map(value => !!value),
			);
			obs.push(pipe);
		}
		return obs;
	}

	private pushIfAbsent(input: { key: string; title: string; }): void {
		if (!this.missingValueKeyLabelPairs.some(p => p.key === input.key)) {
			this.missingValueKeyLabelPairs.push(input);
		}
	}

	private pushArrayIfAbsent(inputs: { key: string; title: string }[]): void {
		for (const input of inputs) {
			this.pushIfAbsent(input);
		}
	}
}
